import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import useAuth from "../hook/useAuth";
import useGroup from "../hook/useGroup";
import useConfirm from "../hook/useConfirm";
import { post_SendLeadershipSMS, post_SendMinistrySMS } from "../services/SendSms";

type Props = {
    open: boolean;
    onClose: () => void;
    entityType: "Leadership" | "Ministry";
    entityId: number;
    entityName: string;
}

const SendSms = ({
    open,
    onClose,
    entityType,
    entityId,
    entityName
}: Props) => {
    const token = Cookies.get("token-my-cdir");

    const { auth }: any = useAuth();
    const { selectedGroupId, isSuperAdmin }: any = useGroup();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();

    const [message, setMessage] = useState<string>("");

    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const [characterCount, setCharacterCount] = useState<number>(0);

    useEffect(() => {
        setCharacterCount(message.length);
    }, [message]);

    const closeModal = () => {
        onClose();
        resetState();
    }

    const resetState = () => {
        setMessage("");
        setCharacterCount(0);
        setIsProcessing(false);
    }

    const handleCancel = () => {
        if (isProcessing) return;

        // If there is no message, close directly.
        if (!message.trim()) {
            closeModal(); return;
        }

        setConfirmPopup(true);
        setConfirmData({
            title: "Cancel SMS",
            summary: "Are you sure you want to cancel? Your message will be lost.",
            confirmBtn: "Yes, Cancel",
            isCancelBtn: true,
            isConfirm: (cb: any) => {
                closeModal(); cb();
            }
        });
    }

    const getSegmentCount = () => {
        if (!message.length) {
            return 0;
        }

        // Basic GSM SMS calculation.
        // 160 chars for a single SMS and
        // 153 chars per segment for multiple SMS.
        if (message.length <= 160) {
            return 1;
        }

        return Math.ceil(message.length / 153);
    }

    const segmentCount = getSegmentCount();

    const sendSms = async (cb: any) => {
        setIsProcessing(true);

        try {
            if (entityType === "Leadership") {
                await post_SendLeadershipSMS(
                    selectedGroupId,
                    entityId,
                    message.trim(),
                    isSuperAdmin,
                    auth?.id,
                    token!
                );
            } else if (entityType === "Ministry") {
                await post_SendMinistrySMS(
                    selectedGroupId,
                    entityId,
                    message.trim(),
                    isSuperAdmin,
                    auth?.id,
                    token!
                );
            }

            closeModal(); cb();
        } catch (error: any) {
            console.error("Error sending SMS:", error);
            setIsProcessing(false);

            // Close the confirmation popup. 
            cb();

            setTimeout(() => {
                setConfirmPopup(true);

                const data = error.response?.data;
                let errorMessage = data?.message ?? "Failed to send SMS. Please try again.";

                if (data?.code === "WALLET_NOT_FOUND" || data?.code === "INSUFFICIENT_WALLET_BALANCE") {
                    errorMessage = data.warning;
                }

                setConfirmData({
                    title: "SMS Failed",
                    summary: errorMessage,
                    confirmBtn: "OK",
                    isCancelBtn: false,

                    isConfirm: (confirmCb: any) => {
                        confirmCb();
                    }
                });
            }, 100);
        }
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!message.trim() || isProcessing) {
            return;
        }

        setConfirmPopup(true);

        setConfirmData({
            title: "Send SMS",
            summary: `Are you sure you want to send this SMS to ${entityName}? This action cannot be undone.`,
            confirmBtn: "Send",
            isCancelBtn: true,
            isConfirm: async (cb: any) => {
                await sendSms(cb);
            }
        });
    }

    const messageRef = useRef<HTMLTextAreaElement | null>(null);

    const mergeTags =
        entityType === "Leadership"
            ? [
                { label: "First Name", value: "{{FirstName}}" },
                { label: "Last Name", value: "{{LastName}}" },
                { label: "Full Name", value: "{{FullName}}" },
                { label: "Email", value: "{{Email}}" },
                { label: "Phone", value: "{{PhoneNumber}}" },
                { label: "Designation", value: "{{Designation}}" },
            ]
            : [
                { label: "First Name", value: "{{FirstName}}" },
                { label: "Last Name", value: "{{LastName}}" },
                { label: "Full Name", value: "{{FullName}}" },
                { label: "Email", value: "{{Email}}" },
                { label: "Phone", value: "{{PhoneNumber}}" },
                { label: "Birthday", value: "{{Birthday}}" },
                { label: "Username", value: "{{UserName}}" }
            ];

    const insertMergeTag = (tag: string) => {
        const textarea = messageRef.current;

        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        const updatedMessage =
            message.substring(0, start) +
            tag +
            message.substring(end);

        setMessage(updatedMessage);

        requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(
                start + tag.length,
                start + tag.length
            );
        });
    }

    return (
        <div className={`fixed inset-0 z-[1000] ${open ? "flex" : "hidden"} items-start justify-center bg-black/50 p-3 backdrop-blur-sm sm:p-6`}>
            <form
                onSubmit={handleSubmit}
                className="relative flex max-h-[94vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            >
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-5 sm:px-7">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                className="h-6 w-6"
                            >
                                <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5Z" />
                            </svg>
                        </div>

                        <div className="min-w-0">
                            <h2 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                                Compose SMS
                            </h2>
                            <p className="mt-1 truncate text-sm text-slate-500">
                                Send a text to{" "}
                                <span className="font-semibold text-slate-700">
                                    {entityName}
                                </span>
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isProcessing}
                        aria-label="Close SMS form"
                        className="ml-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="h-5 w-5"
                        >
                            <path d="m18 6-12 12M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-7">
                    <div className="flex items-start gap-3 rounded-xl border border-secondary/20 bg-secondary/5 p-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-secondary shadow-sm">
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                className="h-5 w-5"
                            >
                                <circle cx="12" cy="8" r="4" />
                                <path d="M4 21a8 8 0 0 1 16 0" />
                            </svg>
                        </div>

                        <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold uppercase tracking-wider text-secondary">
                                Recipient
                            </p>
                            <p className="mt-1 break-words text-sm font-semibold text-slate-900">
                                {entityName || "Not provided"}
                            </p>
                        </div>

                        <span className="shrink-0 rounded-full border border-secondary/20 bg-white px-2.5 py-1 text-xs font-medium text-secondary">
                            SMS
                        </span>
                    </div>

                    <div className="overflow-hidden rounded-xl border border-secondary/20 bg-secondary/5">
                        <div className="flex items-start gap-3 border-b border-secondary/15 px-4 py-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-secondary shadow-sm">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    className="h-5 w-5"
                                >
                                    <path d="M8 8 4 12l4 4M16 8l4 4-4 4M14 5l-4 14" />
                                </svg>
                            </div>

                            <div>
                                <p className="text-sm font-semibold text-slate-800">
                                    Personalize your SMS
                                </p>
                                <p className="mt-1 text-xs leading-5 text-slate-500">
                                    Select a tag to insert it at the cursor in your message.
                                </p>
                            </div>
                        </div>

                        <div className="p-3">
                            <div className="mb-3 flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    Available fields
                                </span>

                                <span className="rounded-full bg-secondary/10 px-2.5 py-1 text-[10px] font-semibold text-secondary">
                                    {entityType}
                                </span>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {
                                    mergeTags.map((tag) => (
                                        <button
                                            key={tag.value}
                                            type="button"
                                            title={`Insert ${tag.value}`}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => insertMergeTag(tag.value)}
                                            className="inline-flex items-center gap-1 rounded-lg border border-secondary/20 bg-white px-3 py-2 text-xs font-medium text-secondary transition hover:border-secondary hover:bg-secondary hover:text-white active:scale-[0.98]"
                                        >
                                            <span className="opacity-70">{"{{"}</span>
                                            {tag.label}
                                            <span className="opacity-70">{"}}"}</span>
                                        </button>
                                    ))
                                }
                            </div>

                            <p className="mt-3 text-[11px] leading-4 text-slate-500">
                                Example: Hello {"{{FirstName}}"}, welcome!
                            </p>
                        </div>
                    </div>

                    <div>
                        <div className="mb-2 flex items-center justify-between gap-3">
                            <label
                                htmlFor="smsMessage"
                                className="text-sm font-semibold text-slate-700"
                            >
                                Message <span className="text-red-500">*</span>
                            </label>

                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                                {characterCount} characters
                            </span>
                        </div>

                        <textarea
                            ref={messageRef}
                            id="smsMessage"
                            name="smsMessage"
                            required
                            rows={6}
                            maxLength={1600}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Type your SMS message here..."
                            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-secondary focus:ring-4 focus:ring-secondary/10"
                        />

                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    className="h-4 w-4"
                                >
                                    <rect x="5" y="3" width="14" height="18" rx="2" />
                                    <path d="M9 7h6M9 11h6M9 15h3" />
                                </svg>
                                {segmentCount} SMS segment{segmentCount !== 1 ? "s" : ""}
                            </span>

                            <span className="text-xs text-slate-400">
                                Maximum 1600 characters
                            </span>
                        </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-center justify-between gap-3 text-sm">
                            <span className="text-slate-500">Recipient</span>
                            <span className="max-w-[65%] truncate font-semibold text-slate-800">
                                {entityName || "Not provided"}
                            </span>
                        </div>

                        <div className="mt-3 flex items-center justify-between gap-3 text-sm">
                            <span className="text-slate-500">SMS segments</span>
                            <span className="rounded-full bg-secondary/10 px-2.5 py-1 text-xs font-semibold text-secondary">
                                {segmentCount}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="border-t border-slate-200 bg-slate-50/80 px-5 py-4 sm:px-7">
                    <p className="mb-4 flex items-start gap-2 text-xs leading-5 text-slate-500">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.7"
                            className="mt-0.5 h-4 w-4 shrink-0"
                        >
                            <path d="M12 9v4m0 4h.01" />
                            <path d="M10.3 3.9 2.5 17.4A2 2 0 0 0 4.2 20h15.6a2 2 0 0 0 1.7-2.6L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                        </svg>
                        Review your message before sending. SMS messages cannot be recalled once sent.
                    </p>

                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            disabled={isProcessing}
                            onClick={handleCancel}
                            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-32"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={isProcessing || !message.trim()}
                            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-secondary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-custom-blue hover:shadow disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-40"
                        >
                            {
                                isProcessing
                                    ? (
                                        <>
                                            <svg
                                                className="h-4 w-4 animate-spin"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="9"
                                                    stroke="currentColor"
                                                    strokeWidth="3"
                                                    opacity=".25"
                                                />
                                                <path
                                                    d="M21 12a9 9 0 0 0-9-9"
                                                    stroke="currentColor"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.8"
                                                className="h-4 w-4"
                                            >
                                                <path d="m22 2-7 20-4-9-9-4Z" />
                                                <path d="M22 2 11 13" />
                                            </svg>
                                            Send SMS
                                        </>
                                    )
                            }
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default SendSms;
